import { Controller } from "@hotwired/stimulus";

// Fills the end time and the registration deadline as an offset from the start time, picked from
// preset buttons. A preset follows the start time when it changes; typing a time switches to custom.
export default class extends Controller {
  static targets = ["startsAt", "field", "preset"];
  static values = { newRecord: Boolean };

  connect() {
    this.fieldTargets.forEach((field) => {
      const hours = this.newRecordValue && !field.value ? this.defaultHours(field) : this.matchingHours(field);
      this.select(field, hours);
    });
  }

  startsAtChanged() {
    this.fieldTargets.forEach((field) => this.apply(field));
  }

  fieldEdited(event) {
    this.select(event.target, null);
  }

  pick(event) {
    const field = this.fieldTargets.find((target) => target.dataset.field === event.params.field);
    const hours = event.params.hours === "custom" ? null : Number(event.params.hours);
    this.select(field, hours);
    if (hours === null) field.focus();
  }

  select(field, hours) {
    field.dataset.hours = hours === null ? "" : hours;
    this.presetsFor(field).forEach((button) => {
      const preset = button.dataset.eventTimesHoursParam;
      const active = preset === "custom" ? hours === null : Number(preset) === hours;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", active);
    });
    this.apply(field);
  }

  apply(field) {
    if (field.dataset.hours === "") return;

    const value = this.shifted(field, Number(field.dataset.hours));
    if (value) field.value = value;
  }

  // Wall-clock arithmetic, so "24h before 6 PM" is 6 PM the day before even across a daylight-saving change.
  shifted(field, hours) {
    const start = this.parse(this.startsAtTarget.value);
    if (!start) return null;

    const direction = field.dataset.field === "registrationClosesAt" ? -1 : 1;
    start.setHours(start.getHours() + direction * hours);
    return this.format(start);
  }

  presetsFor(field) {
    return this.presetTargets.filter((button) => button.dataset.eventTimesFieldParam === field.dataset.field);
  }

  defaultHours(field) {
    return field.dataset.field === "registrationClosesAt" ? 24 : 3;
  }

  matchingHours(field) {
    const value = this.parse(field.value);
    if (!value) return null;

    const match = this.presetsFor(field)
      .map((button) => Number(button.dataset.eventTimesHoursParam))
      .find((hours) => !Number.isNaN(hours) && this.shifted(field, hours) === this.format(value));
    return match ?? null;
  }

  parse(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // datetime-local fields take local time as YYYY-MM-DDTHH:MM.
  format(date) {
    const pad = (number) => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
